/**
 * Phase 6e: Bookmaker Links Button
 *
 * Dropdown button that shows links to place bet at various bookmakers.
 */

import React, { useState, useRef, useEffect } from 'react'
import { IoOpenOutline } from 'react-icons/io5'
import { getBookmakerLinks, type BookmakerLink } from '../api'

interface BookmakerLinksButtonProps {
  fixtureId?: number
  marketType?: string
  selection?: string
  className?: string
}

export function BookmakerLinksButton({
  fixtureId,
  marketType,
  selection,
  className = '',
}: BookmakerLinksButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [links, setLinks] = useState<BookmakerLink[]>([])
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Fetch links when dropdown opens
  useEffect(() => {
    if (isOpen && links.length === 0) {
      setLoading(true)
      getBookmakerLinks({ fixture_id: fixtureId, market_type: marketType, selection })
        .then(({ links }) => setLinks(links))
        .catch((err) => console.error('Failed to fetch bookmaker links:', err))
        .finally(() => setLoading(false))
    }
  }, [isOpen, fixtureId, marketType, selection, links.length])

  const handleLinkClick = (link: BookmakerLink) => {
    // Open in new tab
    window.open(link.url, '_blank', 'noopener,noreferrer')
    setIsOpen(false)
  }

  return (
    <div className={`bookmaker-links ${className}`} ref={dropdownRef}>
      <button
        className="bookmaker-links-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title="Open in bookmaker"
      >
        <IoOpenOutline size={14} />
        <span>Place Bet</span>
      </button>

      {isOpen && (
        <div className="bookmaker-links-dropdown">
          <div className="bookmaker-links-header">Open in bookmaker</div>
          <div className="bookmaker-links-list">
            {loading ? (
              <div className="bookmaker-links-loading">Loading...</div>
            ) : links.length === 0 ? (
              <div className="bookmaker-links-empty">No bookmakers available</div>
            ) : (
              links.map((link) => (
                <button
                  key={link.bookmaker_slug}
                  className="bookmaker-link-item"
                  onClick={() => handleLinkClick(link)}
                >
                  <span className="bookmaker-link-name">{link.bookmaker_name}</span>
                  <IoOpenOutline size={12} className="bookmaker-link-icon" />
                </button>
              ))
            )}
          </div>
          <div className="bookmaker-links-footer">
            Opens bookmaker site in new tab
          </div>
        </div>
      )}
    </div>
  )
}
